import { GridFilterModel, GridLogicOperator } from "@mui/x-data-grid-pro";

import {
  FilterGroupState,
  FilterRowState,
} from "features/ui/Filters/FilterBuilder/types";
import { FilterOperator } from "features/ui/Filters/types";
import { SchemaEntry } from "features/ui/MaterialTable";

const supportsHasOperators = (schemaEntry: SchemaEntry): boolean =>
  !!schemaEntry.filter?.isCount;

/**
 * Convert API operators back to UI operators for display in the MUI DataGrid filter panel.
 * This is needed because some UI operators (like HAS_ONE_OR_MORE, HAS_NONE) are converted
 * to different API operators (gt, lte) when stored, and we need to convert them back
 * for proper display.
 */
const convertAPIOperatorToUIOperator = (
  operator: string,
  values: string[],
  fieldSupportsHasOperators: boolean
): string => {
  if (!fieldSupportsHasOperators) {
    return operator;
  }

  if (
    operator === FilterOperator.GREATER_THAN &&
    values.length === 1 &&
    values[0] === "0"
  ) {
    return FilterOperator.HAS_ONE_OR_MORE;
  }

  if (
    operator === FilterOperator.LESS_OR_EQUAL &&
    values.length === 1 &&
    values[0] === "0"
  ) {
    return FilterOperator.HAS_NONE;
  }

  return operator;
};

export const convertFilterGroupStateToGridFilterModel = (
  filterGroupState?: FilterGroupState,
  schema?: SchemaEntry[]
): GridFilterModel => {
  if (!filterGroupState)
    return { items: [], logicOperator: GridLogicOperator.And };

  const hasOperatorFieldsSet = new Set(
    schema
      ?.filter((entry) => supportsHasOperators(entry))
      .map((entry) => entry.filter?.fieldName || entry.accessor) || []
  );

  return {
    items: filterGroupState.children
      .filter((child) => child.type === "row")
      .map((child) => child as FilterRowState)
      .filter(
        (row) =>
          row.operator && row.values && row.values.length > 0 && row.attribute
      )
      .map((row) => ({
        id: row.id,
        field: row.attribute as string,
        operator: convertAPIOperatorToUIOperator(
          row.operator as string,
          row.values as string[],
          hasOperatorFieldsSet.has(row.attribute as string)
        ),
        value: row.values,
      })),
    logicOperator: GridLogicOperator.And,
  };
};
